import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { SocialMediaPlannerService } from '../../services/socialMediaPlannerService';
import { Campaign } from '../../types/socialMediaPlanner';

interface CampaignListProps {
  onSelectCampaign?: (campaign: Campaign) => void;
  onCreateCampaign?: () => void;
  className?: string;
}

/**
 * Displays a list of campaigns with options to select, edit, or delete
 */
const CampaignList: React.FC<CampaignListProps> = ({ 
  onSelectCampaign, 
  onCreateCampaign,
  className = '' 
}) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const plannerService = new SocialMediaPlannerService();

  // Fetch campaigns on component mount
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        const data = await plannerService.getCampaigns();
        setCampaigns(data);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching campaigns:', err);
        setError(err?.userMessage || 'Failed to load campaigns');
        toast.error('Failed to load campaigns');
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  const handleDeleteCampaign = async (campaignId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the row click
    if (window.confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) {
      try {
        await plannerService.deleteCampaign(campaignId);
        toast.success('Campaign deleted successfully');
        setCampaigns(campaigns.filter(campaign => campaign.id !== campaignId));
      } catch (err: any) {
        console.error('Error deleting campaign:', err);
        toast.error(err?.userMessage || 'Failed to delete campaign');
      }
    }
  };

  const getCampaignStatusClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className={`flex justify-center items-center p-8 ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center p-6 bg-red-50 rounded-lg ${className}`}>
        <p className="text-red-700">{error}</p>
        <button 
          className="mt-3 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className={`text-center p-8 bg-gray-50 rounded-lg ${className}`}>
        <h3 className="text-xl font-semibold text-gray-700 mb-4">No Campaigns Yet</h3>
        <p className="text-gray-600 mb-6">
          Create your first campaign to organize and manage your social media posts.
        </p>
        <button
          onClick={onCreateCampaign}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
        >
          Create Campaign
        </button>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Campaigns</h2>
        <button
          onClick={onCreateCampaign}
          className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition flex items-center"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          New Campaign
        </button>
      </div>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Posts
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {campaigns.map((campaign) => (
              <tr 
                key={campaign.id} 
                onClick={() => onSelectCampaign && onSelectCampaign(campaign)}
                className="hover:bg-gray-50 cursor-pointer"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {campaign.name}
                      </div>
                      {campaign.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {campaign.description}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-full ${getCampaignStatusClass(campaign.status || 'draft')}`}>
                    {campaign.status || 'draft'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {campaign.posts?.length || 0}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button 
                    onClick={(e) => handleDeleteCampaign(campaign.id || '', e)}
                    disabled={!campaign.id}
                    className="text-red-600 hover:text-red-800 ml-4 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CampaignList;
